'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["cancelText", "isEmailFieldDisabled", "isExpanded", "disabled", "isLoading", "onCancel", "onSubmit", "serverValidationErrors", "submitText", "value", "isAdminTemplate"];
import PropTypes from 'prop-types';
import { PureComponent } from 'react';
import emptyFunction from 'react-utils/emptyFunction';
import { Map as ImmutableMap } from 'immutable';
import text from 'I18n/utils/unescapedText';
import debounce from 'transmute/debounce';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { syntheticEvent } from 'foundations-components/transitional/utils/SyntheticEvent';
import { AccordionItem } from 'foundations-components/transitional/components/AccordionItem';
import { Form } from 'foundations-components/transitional/components/Form';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { Checkbox } from 'foundations-components/transitional/components/Checkbox';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import Field from './component/Field';
import AutofillHoneypot from './component/AutofillHoneypot';
import { getServerValidationMessages, isValidEmail, isValidHost, isValidPort } from 'InboxConnectUtils/utils/Validation';
import { isNonEmptyString } from '../../utils/StringValidation';
import AccountSettings from './section/AccountSettings';
import InboxFormActions from './section/InboxFormActions';
import ImapServerSettings from './section/ImapServerSettings';
import SecuritySettings from './section/SecuritySettings';
import { filterFalseyValuesFromObject } from '../../utils/ObjectUtils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const LANG_ROUTE = 'InboxConnectUtils.genericInboxForm';
const isValidPassword = isNonEmptyString;
const advancedFields = ['imapHost', 'imapPort', 'smtpHost', 'smtpPort', 'acceptUntrustedCert'];
const errorBelowFold = validationMessages => Object.keys(validationMessages).some(key => advancedFields.includes(key));
const calcServerValidationState = ({
  imapHost,
  smtpHost,
  smtpUsername,
  smtpSameCreds
} = {}) => ({
  isImapHostValid: imapHost ? isValidHost(imapHost) : null,
  isSmtpHostValid: smtpHost ? isValidHost(smtpHost) : null,
  isSmtpUsernameValid: !smtpSameCreds ? isNonEmptyString(smtpUsername) : null
});
export default class GenericInboxForm extends PureComponent {
  constructor() {
    super();
    this.state = {
      didExpandAdvancedOptions: false,
      isEmailValid: null,
      isImapHostValid: null,
      isSmtpHostValid: null,
      isSmtpUsernameValid: null,
      mostRecentlyValidatedEmail: ''
    };
    this.getIsSubmittable = this.getIsSubmittable.bind(this);
    this.handleFieldChange = this.handleFieldChange.bind(this);
    this.validateInput = debounce(500, this.validateInput.bind(this));
  }
  componentDidMount() {
    const {
      value: {
        email
      } = {}
    } = this.props;
    const nextState = Object.assign({
      isEmailValid: email ? isValidEmail(email) : null
    }, calcServerValidationState(this.props.value));
    this.setState(nextState);
  }
  componentDidUpdate(prevProps) {
    const {
      value: {
        imapHost: nextImapHost,
        smtpHost: nextSmtpHost
      }
    } = this.props;
    const {
      value: {
        imapHost,
        smtpHost
      }
    } = prevProps;
    const didAutoDiscoveryOccur = nextImapHost !== imapHost || nextSmtpHost !== smtpHost;
    if (didAutoDiscoveryOccur) {
      this.setState(Object.assign({}, calcServerValidationState(this.props.value)));
    }
  }
  componentWillUnmount() {
    this.validateInput.cancel();
  }
  getIsSubmittable() {
    const {
      disabled,
      isLoading,
      serverValidationErrors,
      value
    } = this.props;
    return !disabled && !isLoading && this.state.isEmailValid && this.state.isImapHostValid && this.state.isSmtpHostValid && (value.smtpSameCreds || this.state.isSmtpUsernameValid) && isValidPassword(value.password) && isValidPort(value.imapPort) && isValidPort(value.smtpPort) && (serverValidationErrors.get('UNTRUSTED_CERTIFICATES') ? value.acceptUntrustedCert === true : true);
  }
  getValidationState(value, prevState, handleDiscover) {
    const isEmailValid = value.email ? isValidEmail(value.email) : null;
    if (handleDiscover) {
      const didEmailJustBecomeValid = !prevState.isEmailValid && isEmailValid;
      const didEmailJustChangeToDifferentValidValue = value.email !== prevState.mostRecentlyValidatedEmail && isEmailValid;
      if (didEmailJustBecomeValid || didEmailJustChangeToDifferentValidValue) {
        handleDiscover({
          email: value.email
        });
      }
    }
    return Object.assign({
      isEmailValid,
      mostRecentlyValidatedEmail: value.email || ''
    }, calcServerValidationState(value));
  }
  validateInput(value) {
    this.setState(state => this.getValidationState(value, state, this.props.handleDiscover));
  }
  handleValueChange(nextValue) {
    const {
      onChange
    } = this.props;
    onChange(syntheticEvent(nextValue));
    this.validateInput(nextValue);
  }
  handleFieldChange({
    target: {
      value: fieldValue
    }
  }, fieldName) {
    const {
      value
    } = this.props;
    const nextValue = value.set(fieldName, fieldValue);
    this.handleValueChange(nextValue);
  }
  render() {
    const _this$props = this.props,
      {
        cancelText,
        isEmailFieldDisabled,
        isExpanded,
        disabled,
        isLoading,
        onCancel,
        onSubmit,
        serverValidationErrors,
        submitText,
        value,
        isAdminTemplate
      } = _this$props,
      rest = _objectWithoutPropertiesLoose(_this$props, _excluded);
    const {
      didExpandAdvancedOptions,
      isEmailValid,
      isImapHostValid,
      isSmtpHostValid,
      isSmtpUsernameValid
    } = this.state;
    delete rest.handleDiscover;
    delete rest.onChange;
    const isSubmittable = this.getIsSubmittable();
    const serverSettingsSharedProps = {
      autoCapitalize: 'off',
      autoComplete: 'false',
      autoCorrect: 'off',
      disabled: disabled || isAdminTemplate,
      keyPrefix: LANG_ROUTE,
      onChange: this.handleFieldChange,
      required: true,
      spellCheck: false
    };
    const getValidationMessage = (validationCheck, errorKeySuffix) => {
      return validationCheck !== null && (validationCheck ? undefined : `${LANG_ROUTE}.${errorKeySuffix}`);
    };
    const clientValidationMessages = {
      email: getValidationMessage(isEmailValid, 'emailInvalid'),
      imapHost: getValidationMessage(isImapHostValid, 'incomingServer.hostInvalid'),
      smtpHost: getValidationMessage(isSmtpHostValid, 'outgoingServer.hostInvalid'),
      smtpUsername: getValidationMessage(isSmtpUsernameValid, 'outgoingServer.usernameInvalid')
    };
    const validationMessages = filterFalseyValuesFromObject(Object.assign({}, getServerValidationMessages(serverValidationErrors), clientValidationMessages));
    const forceAdvancedOpen = errorBelowFold(validationMessages);
    const changeMatchCheckbox = ({
      target: {
        checked
      }
    }, fieldName) => {
      let nextValue = value.set(fieldName, checked);
      if (!checked && !value.customUsername) {
        nextValue = nextValue.set('customUsername', value.email);
      } else if (checked && value.customUsername === value.email) {
        nextValue = nextValue.set('customUsername', '');
      }
      this.handleValueChange(nextValue);
    };
    const splitCredzCheck = () => /*#__PURE__*/_jsx(Field, Object.assign({}, serverSettingsSharedProps, {
      "aria-hidden": !isExpanded,
      checked: value.smtpSameCreds,
      Component: Checkbox,
      messageKeySuffix: "",
      name: "smtpSameCreds",
      onChange: changeMatchCheckbox,
      value: "",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: `${LANG_ROUTE}.outgoingServer.customCredentials`
      })
    }));
    const serverSettings = /*#__PURE__*/_jsxs(AccordionItem, {
      marginBlockStart: "1000",
      textAlign: "start",
      disabled: isExpanded || forceAdvancedOpen,
      onOpenChange: () => {
        this.setState({
          didExpandAdvancedOptions: !didExpandAdvancedOptions
        });
      }
      // open prop does not work when `disabled` is true
      ,
      defaultOpen: isExpanded || didExpandAdvancedOptions || forceAdvancedOpen,
      title: text(`${LANG_ROUTE}.advancedSettingsHeading`),
      children: [splitCredzCheck(), !value.smtpSameCreds ? null : /*#__PURE__*/_jsx(Field, Object.assign({}, serverSettingsSharedProps, {
        "aria-hidden": !isExpanded,
        helpKeySuffix: "optionalField",
        messageKeySuffix: "customUsernameLabel",
        name: "customUsername",
        required: false,
        value: value.customUsername
      })), /*#__PURE__*/_jsx(ImapServerSettings, {
        isExpanded: isExpanded,
        value: value,
        fieldProps: serverSettingsSharedProps,
        validationMessages: validationMessages,
        showCredentials: !value.smtpSameCreds
      }), /*#__PURE__*/_jsx(SecuritySettings, {
        fieldProps: serverSettingsSharedProps,
        handleFieldChange: this.handleFieldChange,
        isExpanded: isExpanded,
        validationMessages: validationMessages,
        value: value
      })]
    });
    return /*#__PURE__*/_jsxs(Form, Object.assign({
      action: "",
      onSubmit: !disabled && isSubmittable ? onSubmit : emptyFunction
    }, rest, {
      children: [/*#__PURE__*/_jsx(AccountSettings, {
        onFieldChange: this.handleFieldChange,
        isEmailFieldDisabled: isEmailFieldDisabled,
        isPasswordFieldDisabled: !value.smtpSameCreds,
        validationMessages: validationMessages,
        value: value
      }), /*#__PURE__*/_jsx(Tooltip, {
        disabled: !isAdminTemplate,
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: `${LANG_ROUTE}.isAdminTemplate`
        }),
        children: serverSettings
      }), /*#__PURE__*/_jsx(AutofillHoneypot, {}), isLoading && /*#__PURE__*/_jsx(LoadingSpinner, {
        marginBlockStart: "1000",
        grow: true
      }), /*#__PURE__*/_jsx(InboxFormActions, {
        onSubmit: onSubmit,
        onCancel: onCancel,
        submitText: submitText,
        cancelText: cancelText,
        isSubmittable: isSubmittable
      })]
    }));
  }
}
GenericInboxForm.defaultProps = {
  isEmailFieldDisabled: false,
  isLoading: false,
  isExpanded: false,
  serverValidationErrors: ImmutableMap(),
  isAdminTemplate: false
};
GenericInboxForm.propTypes = {
  cancelText: PropTypes.node,
  disabled: PropTypes.bool,
  isEmailFieldDisabled: PropTypes.bool,
  handleDiscover: PropTypes.func,
  isLoading: PropTypes.bool,
  isExpanded: PropTypes.bool,
  onCancel: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func,
  serverValidationErrors: PropTypes.instanceOf(ImmutableMap).isRequired,
  submitText: PropTypes.node,
  value: PropTypes.object.isRequired,
  isAdminTemplate: PropTypes.bool
};